import { MDBIcon } from 'mdb-react-ui-kit';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { AdvancedSettingsModal } from '../../../../../../../components/modals/AdvancedSettingsModal';
import API from '../../../../../../../service/api';
import { MachineAction } from '../../../../../../../components/editable/MachineAction';
import { MachinePowerButton } from '../../../../../../../components/MachinePowerButton';
import { machinePowerStatus } from '../../../../../../../service/status';
import { Alert } from '../../../../../../../components/Alert';
import { CustomPopover } from '../../../../../../../components/popover/CustomPopover';
import { AdvancedSettings } from '../../../../../../../components/modals/advanced-settings/AdvancedSettings';
import { ActionModal } from '../../../../../../../components/modals/ActionModal';
import {
    MachineOrBoxActionDTO,
    OpMachineActionsDTO,
    OpMachineDTO,
} from '@scm/common-legacy';

export interface StatusBarActionPopupProps {
    groupId: number;
    locationId: number;
    machine: OpMachineDTO;
    apiURI: string;
}
export const StatusBarActionPopup = ({
    groupId,
    locationId,
    machine,
    apiURI,
}: StatusBarActionPopupProps) => {
    const { t } = useTranslation();
    const mid = useMemo(() => machine.id, [machine]);

    const [advancedSettings, setAdvancedSettings] = useState(false);
    const [activate, setActivate] = useState(false);
    const [selectedAction, setSelectedAction] = useState<any | null>(null);
    const powerStatus = useMemo(
        () => machinePowerStatus(machine?.power),
        [machine],
    );

    const [actions, setActions] = useState<MachineOrBoxActionDTO[] | null>(
        null,
    );
    const [params, setParams] = useState<boolean | null>(null);
    const [error, setError] = useState(null);
    const [popupOpened, setPopupOpened] = useState(false);
    const serviceMode = useMemo(() => machine.serviceMode === true, [machine]);
    const canAdvancedSettings = useMemo(
        () =>
            machine.connection == true &&
            params &&
            (serviceMode ||
                !machine.expireAt ||
                new Date(machine.expireAt).getTime() > new Date().getTime()),
        [machine.connection, params, machine.expireAt, serviceMode],
    ); //useMemo(() => true, []); // TODOuseMemo(() => params && machine?.power == true && machine?.connection == true, [machine, params]);
    const canActivate = useMemo(
        () => params && machine.expireAt == null,
        [params, machine.expireAt],
    );
    const loading = useMemo(
        () => (actions == null || params == null) && !error,
        [actions, params, error],
    );

    const fetch = useCallback(() => {
        setActions(null);
        setParams(null);
        setError(null);
        API.GET(`${apiURI}/getactions`)
            .then((res: OpMachineActionsDTO) => {
                setParams(res.params);
                setActions(res.actions);
            })
            .catch((err) => setError(err.message));
    }, [mid]);

    const onPopupClose = useCallback(() => {
        setActions(null);
        setParams(null);
        setError(null);
        setPopupOpened(false);
    }, []);

    const onPopupOpen = useCallback(() => {
        fetch();
        setPopupOpened(true);
    }, []);

    return (
        <React.Fragment>
            <AdvancedSettings
                open={advancedSettings}
                onClose={() => setAdvancedSettings(false)}
                groupId={groupId}
                locationId={locationId}
                machineId={mid}
            />
            <ActionModal
                open={activate}
                onClose={() => setActivate(false)}
                title={t('machine:activate')}
                confirmText={t('confirm')}
                action={async () => await API.POST(`${apiURI}/activate`)}
                canSave={true}
            >
                <span>{t('machine:activate_prompt')}</span>
            </ActionModal>

            {selectedAction && (
                <MachineAction
                    action={selectedAction}
                    onDone={async () => {
                        await API.POST(`${apiURI}/action`, {
                            id: selectedAction.id,
                        });
                        setSelectedAction(null);
                    }}
                    autoOpen
                />
            )}

            <CustomPopover
                trigger={
                    <MachinePowerButton
                        machine={machine}
                        tooltipId="sbar-power-tooltip"
                    />
                }
                placement="top-end"
                popoverZIndex={100}
                onOpen={onPopupOpen}
                onClose={onPopupClose}
            >
                {(close) => (
                    <div className="d-flex flex-column">
                        {loading && <span>loading</span>}
                        {!loading && !error && (
                            <>
                                <span
                                    className={`w-100 text-center text-white ${`bg-${powerStatus.bg}`} px-2`}
                                >
                                    {t(`status:${powerStatus.title}`)}
                                </span>
                                {(actions || []).map((action) => (
                                    <MachineAction
                                        action={action}
                                        onDone={async () => {
                                            close();
                                            await API.POST(`${apiURI}/action`, {
                                                id: action.id,
                                            });
                                            setSelectedAction(null);
                                        }}
                                    >
                                        {(open) => (
                                            <div
                                                key={action.id}
                                                className="custom-select-item"
                                                onClick={open}
                                            >
                                                <MDBIcon
                                                    icon={
                                                        action.icon || 'wrench'
                                                    }
                                                    color="primary"
                                                    className="me-1"
                                                    fixed
                                                />
                                                <span
                                                    style={{
                                                        overflow: 'hidden',
                                                        textOverflow:
                                                            'ellipsis',
                                                    }}
                                                >
                                                    {t(action.title)}
                                                </span>
                                            </div>
                                        )}
                                    </MachineAction>
                                ))}
                                {canAdvancedSettings && (
                                    <div
                                        className="custom-select-item"
                                        style={{
                                            borderTop:
                                                '2px solid rgba(0,0,0,0.2)',
                                        }}
                                        onClick={() => {
                                            close();
                                            setTimeout(
                                                () => setAdvancedSettings(true),
                                                200,
                                            );
                                        }}
                                    >
                                        <MDBIcon
                                            icon="server"
                                            color="primary"
                                            className="me-1"
                                        />
                                        <span
                                            style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {t('advanced_settings')}
                                        </span>
                                    </div>
                                )}
                                {canActivate && (
                                    <div
                                        className="custom-select-item"
                                        onClick={() => {
                                            close();
                                            setTimeout(
                                                () => setActivate(true),
                                                200,
                                            );
                                        }}
                                    >
                                        <MDBIcon
                                            icon="check-double"
                                            color="success"
                                            className="me-1"
                                        />
                                        <span
                                            style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {t('machine:activate')}
                                        </span>
                                    </div>
                                )}
                                {(!actions || actions.length == 0) &&
                                    !canAdvancedSettings && (
                                        <span className="d-flex justify-content-center w-100 fst-italic">
                                            {t('no_actions')}
                                        </span>
                                    )}
                            </>
                        )}
                        {!loading && error && (
                            <Alert
                                type="danger"
                                message={error}
                                className="m-2"
                            />
                        )}
                    </div>
                )}
            </CustomPopover>
        </React.Fragment>
    );
};
